/**
 * The playback engine: every command the player can run, as fibers on one
 * non-concurrent atom, so issuing any command interrupts whatever is running.
 *
 * A plain factory rather than a closure inside the provider. The engine lives
 * as long as the provider mounts it, but nothing in here is React — which is
 * what lets the app-specific seams arrive through one live `read()` instead of
 * a ref per prop, and what makes the whole thing constructible in a test
 * without rendering a tree.
 */
import type {
  AudioItemDetail,
  AudioItemSummary,
  Voice,
} from '@listening/api-contract/legacy-client-types';
import { assertDefined, clamp } from '@listening/utils/ts-utils';
import type { QueryClient } from '@tanstack/react-query';
import { Cause, Duration, Effect, Exit, Latch, Schedule } from 'effect';
import { Atom, type AtomRegistry } from 'effect/unstable/reactivity';
import {
  AudioDetailFetchError,
  audioDetailArgs,
  getAudioDetailFromCache,
  isAudioDetailReadyForStreaming,
} from '../query/audio-detail';
import type { AppRuntime, AppServices, Eq } from '../query/runtime-context';
import {
  asLegacyTtsChunk,
  type ChapterTtsChunk,
  type ChapterTtsChunkParams,
  type DocPackTtsChunk,
  type DocPackTtsChunkParams,
  getChapterTtsChunkEffect,
  docPackChapterId,
  getDocPackTtsChunkEffect,
  type LegacyTtsChunk,
  type PlaybackChunk,
} from '../query/tts-chunks';
import {
  type UserPreferences,
  userPreferencesQueryKey,
  userSummaryQueryKey,
} from '../query/user';
import { userPreferencesArgs } from '../query/user-hooks';
import { resolveCurrentVoice, voicesQueryKey } from '../query/voices';
import {
  audioPreferencesAtom,
  resolveAudioVolume,
} from '../stores/audio-preferences-store';
import {
  docPackReadingPreferencesAtom,
  resolveDocPackReadingClassOverrides,
} from '../stores/doc-pack-reading-preferences-store';
import type {
  DocPackReadingAnchor,
  DocPackReadingChunk,
} from '../doc-pack/reading-plan';
import {
  docPackCatalogArgs,
  docPackPageArgs,
  findDocPackChunkByScan,
  isDocPackVersionUsable,
  warmDocPackPagesCoalesced,
  type DocPackPageFetchMode,
  type LoadedDocPack,
} from '../doc-pack/source';
import {
  docPackChunkCacheKey,
  legacyChunkCacheKey,
  readAudioChunkCache,
  writeAudioChunkCache,
} from './chunk-cache';
import {
  type ChunkFetchError,
  type ChunkFetchPriority,
  createChunkFetcher,
} from './chunk-fetcher';
import { buildNowPlayingMetadata, chapterTitleForId } from './now-playing';
import {
  AudioPoolPlaybackError,
  chunkLoadFailureReason,
  chunkLoadFailureTags,
  countsAsNewChunkEvidence,
  NoLoadedAudioError,
  NoPlayableChunkError,
  PLAYBACK_COMMAND_DEBOUNCE_MS,
  PLAYBACK_LOAD_TIMEOUT_MS,
  PlaybackSelectionInvariantError,
  resolveChunkLoadRecovery,
  type ChunkLoadFailure,
  type PlaybackCommand,
  type PlaybackCommandAtom,
  type PlaybackCommandError,
  type AutomatedPlaybackCommandAtom,
  type AutomatedPlaybackDispatch,
  type PlaybackDispatch,
} from './playback-command';
import {
  playbackBoundaryLogMetadata,
  playbackRecoveryAttemptLogMetadata,
  playbackTtsFetchElapsedMs,
  playbackTtsFetchLogMetadata,
  type PlaybackChunkLogContext,
} from './playback-log';
import {
  type PlaybackSessionStats,
  recordBoundary,
  recordFetch,
  recordRecovery,
  resetPlaybackSessionStats,
} from './playback-stats';
import { noteTargetLoadArgs, type NotePlaybackTarget } from './note-playback';
import { loadChunkOnPlayer } from './player-load';
import type { AudioPoolPlaybackLogMetadata } from './pool-player';
import type { AudioPoolPlayerHandle } from './pool-player-context';
import {
  audioProgressLoadArgs,
  initialPlaybackState,
  type AudioChunkPlayerState,
  isSamePlaybackTarget,
  playbackTargetOf,
  resolveSameItemPlaybackAction,
  type AudioChunkPlayerActions,
  type AudioChunkPlayerStoreHandle,
  type CommonLoadParams,
  type LoadPos,
  type PlaybackTarget,
} from './playback-state';
import {
  decodedPrefetchWarmCount,
  didLegacyAudioChunkSelectionFallback,
  findFirstAudioChunkAtOrAfterChapter,
  docPackAnchorForLegacyChapterStart,
  docPackAnchorForLegacyTtsChunk,
  findNextAudioChunk,
  isLegacyAudioChunkSelectionStale,
  resolveLegacyAudioChunkSelection,
} from './chunk-navigation';
import { setTtsChunkAudioDurationMs } from './playback-preferences';
import { getDocPackPlaybackAnchorForPosition } from './timing-index';

/** Upper bound of the prefetch horizon (`clamp(rate·3+1, 5, 9)`), and the
 * cap for lookahead-depth accounting. */
const MAX_PREFETCH_CHUNKS = 9;

// `performance.mark` is a web API; RN's `performance` may not implement it.
const perfMark = (name: string) => {
  if (typeof performance.mark === 'function') performance.mark(name);
};

type GetSummaryResponse = {
  metering: { is_active_subscription: boolean };
};

/** Every playback record carries this, so one filter finds them all whichever
 * app emitted it. */
const PLAYBACK_LOG_MODULE = { module: 'AudioChunkPlayer' };

export type AudioChunkSourceResolverParams = {
  readonly audioId: string;
  readonly chapterId: string;
  readonly ttsId: string;
  readonly voice: string;
  readonly remoteUrl: string;
};

/** Mobile's offline downloads: a locally cached file for a chunk, if one
 * exists. Returning nothing falls back to the remote URL. */
export type AudioChunkSourceResolver = (
  params: AudioChunkSourceResolverParams,
) => Promise<string | undefined> | string | undefined;

/** Attempts made to populate the audio detail before giving up. */
export const DOCUMENT_SUMMARY_FETCH_ATTEMPTS = 3;

/** First backoff step; each retry doubles it, jittered 0.8x-1.2x so a degraded
 * backend does not take every client's retries in lockstep (see
 * docs/projects/backlog/tts/ensure-tts-clients-exp-backoff-jitter.md). */
const DOCUMENT_SUMMARY_RETRY_BASE = Duration.seconds(1);

/**
 * Resolves the document summary, fetching the audio detail when the query cache
 * does not already have it.
 *
 * The summary and the audio id are re-read before every attempt: another path
 * may populate the cache while this one is backing off, and the id going away
 * means the caller navigated off the document. Either way the attempt succeeds
 * with nothing to do, which ends the retry.
 *
 * Exhausting the attempts is not a failure — the final read decides, exactly as
 * it does when the fetch never ran.
 */
export function makeEnsureDocumentSummary<Summary>({
  readSummary,
  readAudioId,
  fetchAudioDetail,
  onAttemptFailed,
}: {
  readSummary: () => Summary | undefined;
  readAudioId: () => string | undefined;
  fetchAudioDetail: (audioId: string) => Promise<unknown>;
  onAttemptFailed: (error: AudioDetailFetchError, attempt: number) => void;
}) {
  return Effect.suspend(() => {
    let attempt = 0;
    const fetchOnce = Effect.suspend(() => {
      if (readSummary() !== undefined) return Effect.void;
      const audioId = readAudioId();
      if (!audioId) return Effect.void;
      return Effect.tryPromise({
        try: () => fetchAudioDetail(audioId),
        catch: (cause) => new AudioDetailFetchError({ audioId, cause }),
      });
    });

    return fetchOnce.pipe(
      Effect.tapError((error) =>
        Effect.sync(() => {
          onAttemptFailed(error, attempt);
          attempt += 1;
        }),
      ),
      Effect.retry({
        schedule: Schedule.jittered(
          Schedule.exponential(DOCUMENT_SUMMARY_RETRY_BASE),
        ),
        times: DOCUMENT_SUMMARY_FETCH_ATTEMPTS - 1,
      }),
      Effect.ignore,
      Effect.andThen(Effect.sync(readSummary)),
    );
  });
}

/**
 * The app-specific seams, read fresh at every use.
 *
 * The engine outlives every render, so capturing these would freeze them at
 * first mount — which is exactly how `delayToPrefetchMs` came to be read off a
 * stale props object.
 */
export type PlaybackEngineSeams = {
  readonly player: AudioPoolPlayerHandle;
  readonly userLoggedIn: boolean;
  readonly docPackEnabled: boolean;
  readonly docPackPageFetchMode: DocPackPageFetchMode;
  readonly resolveAudioChunkSrc?: AudioChunkSourceResolver | undefined;
  readonly downloadedVoicesAtom?:
    | Atom.Atom<Readonly<Record<string, string>>>
    | undefined;
  readonly readPersistedChunk?:
    | ((
        params: ChapterTtsChunkParams & { audioId: string },
      ) => ChapterTtsChunk | undefined)
    | undefined;
  readonly delayToPrefetchMs?: number | undefined;
  readonly isPremiumUser?: IsPremiumUserSeam | undefined;
  readonly awaitEntitlementReady?: (() => Promise<void>) | undefined;
};

/**
 * The app's one entitlement answer. A load resumes from
 * `awaitEntitlementReady` inside the app store's own notification, so only a
 * thunk reads the answer it waited for; a boolean still holds whatever React
 * last rendered.
 */
export type IsPremiumUserSeam = boolean | (() => boolean);

export const readIsPremiumUserSeam = (seam: IsPremiumUserSeam | undefined) =>
  typeof seam === 'function' ? seam() : seam;

export type PlaybackEngine = {
  readonly commandAtom: PlaybackCommandAtom;
  /** The automated lane. Mounted by the provider like `commandAtom`; nothing
   * outside the engine writes to it. */
  readonly automatedCommandAtom: AutomatedPlaybackCommandAtom;
  readonly actions: AudioChunkPlayerActions;
};

export const createPlaybackEngine = ({
  registry,
  runtime,
  queryClient,
  eq,
  store,
  read,
}: {
  readonly registry: AtomRegistry.AtomRegistry;
  readonly runtime: AppRuntime;
  readonly queryClient: QueryClient;
  readonly eq: Eq;
  readonly store: AudioChunkPlayerStoreHandle;
  readonly read: () => PlaybackEngineSeams;
}): PlaybackEngine => {
  // The atom's body is lazy — it runs when a command is dispatched, long after
  // `runCommand` below is defined — so the cycle between the two needs no
  // forward declaration.
  const commandAtom: PlaybackCommandAtom = Atom.fn<PlaybackDispatch>()(
    (dispatch) => Effect.suspend(() => runCommand(dispatch)),
    { concurrent: false },
  );
  const automatedCommandAtom: AutomatedPlaybackCommandAtom =
    Atom.fn<AutomatedPlaybackDispatch>()(
      (dispatch) => Effect.suspend(() => runAutomatedCommand(dispatch)),
      { concurrent: false },
    );

  // Advances taken because a chunk proved unplayable this incident —
  // permanently missing (404/400) or undecodable after the retry budget.
  // One is allowed (skip genuinely-broken content); a second means the
  // document is broken, not the chunk.
  let incidentAdvanceCount = 0;
  // Armed at a natural chunk end, resolved by the next onCanPlay — the
  // user-perceived boundary gap, silent-retry recovery included. User
  // commands clear it so seeks and fresh loads never count as boundaries.
  let boundaryMarker: {
    startedAtMs: number;
    lookaheadDepth?: number;
  } | null = null;
  const recordPlaybackStats = (
    audioId: string,
    sessionId: number,
    update: (stats: PlaybackSessionStats) => PlaybackSessionStats,
  ) => {
    const state = store.getState();
    if (state.audioId !== audioId || state.stats.sessionId !== sessionId)
      return;
    store.setState({ stats: update(state.stats) });
  };
  /**
   * Opens a user-intent command: a fresh incident, no boundary in progress,
   * and — on a faulted player — a cleared fault plus an immediate circuit
   * probe rather than waiting out the probe interval.
   *
   * Called per command body rather than once at the dispatch site, because it
   * CLEARS the fault: the `play` action and `runLoadAudioEffect` both have to
   * read `playbackFault` first to decide whether the press is retry intent.
   * Automated commands never call it — a silent retry must not masquerade as
   * a user probe.
   */
  const beginPlaybackCommand = () => {
    incidentAdvanceCount = 0;
    boundaryMarker = null;
    if (store.getState().playbackFault !== null) {
      chunkFetcher.noteUserRetry();
      store.setState({ playbackFault: null });
    }
  };
  const isCurrentPlaybackTarget = (target: PlaybackTarget) =>
    isSamePlaybackTarget(target, playbackTargetOf(store.getState()));
  // User command fibers between their start and their finalizer. Read at the
  // dispatch site: the registry finalizes the superseded fiber before the
  // replacement's body runs, so a body always reads zero. `userIdle` is the
  // same fact shaped as something the automated lane can await.
  let userCommandsInFlight = 0;
  const userIdle = Latch.makeUnsafe(true);
  const dispatchPlaybackCommand = (command: PlaybackCommand) => {
    // Every command retargets playback (play/pause are imperative actions, not
    // commands), so an automated advance in flight is loading a chunk the user
    // has just moved away from — interrupt it. The advance is not LOST by the
    // interrupt: `pendingAutomated` survives and is re-dispatched when the
    // user lane drains, where the target check decides whether it still
    // applies.
    registry.set(automatedCommandAtom, Atom.Interrupt);
    registry.set(commandAtom, {
      command,
      supersedes: userCommandsInFlight > 0,
    });
  };
  /**
   * The boundary advance as STATE, not just a fiber: interruption kills the
   * fiber but must not lose the fact that a chunk ended un-advanced — that
   * fact has no other owner, and dropping it stops the document dead.
   * `advancedTo` is recorded once the advance re-points the store, so a rerun
   * knows to finish that chunk's load instead of advancing past it.
   * Cleared only by a COMPLETED lane run (advanced, resumed, or dropped as
   * stale) or by an unload.
   */
  let pendingAutomated: {
    readonly dispatch: AutomatedPlaybackDispatch;
    advancedTo?: PlaybackTarget | undefined;
  } | null = null;
  /** Automated work — today the chunk-boundary advance — goes to a lane of its
   * own, so it neither interrupts the user's command nor is dropped by it: it
   * waits for the user lane to drain, then re-checks the chunk it belonged to.
   * The recovery tail dispatched from inside a failing command is deliberately
   * NOT routed here: it continues the command it belongs to. */
  const dispatchAutomatedCommand = (
    command: PlaybackCommand,
    target: PlaybackTarget | undefined,
  ) => {
    const dispatch: AutomatedPlaybackDispatch = { command, target };
    pendingAutomated = { dispatch };
    registry.set(automatedCommandAtom, dispatch);
  };
  /** Command fibers run under the atom registry's services, not the app
   * runtime's, so they borrow its context: the chunk fetches they drive need
   * its ApiClient/HttpClient, and its logger. Logging belongs INSIDE this
   * bridge — a record outside it reaches the default console logger at the
   * default level (so `logDebug` vanishes), and an `annotateLogs` applied
   * outside is overwritten by the runtime's own bindings. (Decisions §7: this
   * bridge disappears when the fetcher becomes a Layer-provided service.) */
  const withAppServices = (
    body: Effect.Effect<void, PlaybackCommandError, AppServices>,
  ) =>
    Effect.gen(function* () {
      const appServices = yield* runtime.contextEffect;
      return yield* body.pipe(Effect.provideContext(appServices));
    });

  const runCommand = ({ command, supersedes }: PlaybackDispatch) =>
    // The counter is read by the NEXT dispatch, so it is taken in this
    // suspend — ahead of any yield. Incrementing after an await (services,
    // debounce) would let a dispatch landing in that window read zero and skip
    // the debounce.
    Effect.suspend(() => {
      userCommandsInFlight += 1;
      userIdle.closeUnsafe();
      const debounce = supersedes
        ? Effect.sleep(Duration.millis(PLAYBACK_COMMAND_DEBOUNCE_MS))
        : Effect.void;
      return withAppServices(
        debounce.pipe(
          Effect.andThen(runPlaybackCommand(command)),
          // Supersession is not a failure: the fiber that replaced this one
          // owns the loading state now, so only a real fault resets it.
          Effect.tapCause((cause) =>
            isSupersession(cause)
              ? Effect.void
              : Effect.logError('Playback command failed', cause),
          ),
          Effect.annotateLogs(PLAYBACK_LOG_MODULE),
        ),
      ).pipe(
        // Outside the bridge on purpose: the counter is taken before the
        // context is, so an interrupt while acquiring it must still release.
        Effect.ensuring(
          Effect.sync(() => {
            userCommandsInFlight -= 1;
            if (userCommandsInFlight === 0) {
              userIdle.openUnsafe();
              // The dispatch that started this command interrupted any
              // automated fiber; if its work is still owed, re-issue it now.
              // A fresh object because the atom dedupes on reference; the
              // command reference inside is preserved — it is the pending
              // record's identity.
              const pending = pendingAutomated;
              if (pending) {
                registry.set(automatedCommandAtom, { ...pending.dispatch });
              }
            }
          }),
        ),
      );
    });
  /**
   * The automated lane: wait for user intent to drain, then resolve what the
   * work still means against the live store.
   *
   * - Store still on the chunk this work belonged to → run it fresh.
   * - Store on the chunk a previous, interrupted run of this work advanced
   *   to → the selection is ours but its load may have died with the fiber:
   *   finish the load rather than advancing again (which would skip it).
   * - Anywhere else → the user retargeted; the work and any boundary it armed
   *   are stale.
   *
   * Deferring rather than dropping is what keeps a chunk boundary that lands
   * during a user command from stopping the document dead; the target check
   * is what keeps it from advancing past a chunk the user seeked to in the
   * meantime. Automated work never pays the debounce — a boundary advance that
   * slept would be an audible gap.
   */
  const runAutomatedCommand = ({
    command,
    target,
  }: AutomatedPlaybackDispatch) =>
    withAppServices(
      Effect.gen(function* () {
        yield* userIdle.await;
        const pending = pendingAutomated;
        const advancedTo =
          pending?.dispatch.command === command
            ? pending.advancedTo
            : undefined;
        if (target && !isCurrentPlaybackTarget(target)) {
          if (advancedTo && isCurrentPlaybackTarget(advancedTo)) {
            yield* runResumeAdvanceLoad;
            return;
          }
          // The user moved while this waited: the work belongs to a chunk that
          // is no longer playing, and so does any boundary it armed.
          boundaryMarker = null;
          yield* Effect.logDebug(
            'Automated playback command dropped as stale',
          ).pipe(Effect.annotateLogs({ command: command._tag }));
          return;
        }
        yield* runPlaybackCommand(command);
      }).pipe(
        Effect.tapCause((cause) =>
          isSupersession(cause)
            ? Effect.void
            : Effect.logError('Automated playback command failed', cause),
        ),
        // A completed run — advanced, resumed, or dropped as stale — settles
        // the pending record; only interruption leaves it owed. Guarded by
        // command identity so settling never clears a NEWER boundary's record.
        Effect.onExit((exit) =>
          Effect.sync(() => {
            if (!Exit.isSuccess(exit) && isSupersession(exit.cause)) return;
            if (pendingAutomated?.dispatch.command === command) {
              pendingAutomated = null;
            }
          }),
        ),
        Effect.annotateLogs(PLAYBACK_LOG_MODULE),
      ),
    );

  /** A log record from imperative code — transport callbacks, the prefetch
   * promise path, the store actions — run detached on the app runtime, so it
   * reaches the same Effect logger as the records commands `yield*`. */
  const logDetached = (record: Effect.Effect<void>) => {
    runtime.runFork(record.pipe(Effect.annotateLogs(PLAYBACK_LOG_MODULE)));
  };
  /** Detached playback work (prefetch) as a fiber on the app runtime, which
   * provides the services its chunk fetches need and carries the module's log
   * annotation. Errors must be fully handled inside `work`. */
  const forkDetached = (work: Effect.Effect<void, never, AppServices>) => {
    runtime.runFork(work.pipe(Effect.annotateLogs(PLAYBACK_LOG_MODULE)));
  };

  const chunkFetcher = createChunkFetcher({
    runner: runtime,
    onPrefetchDeferred: (key, queueDepth) => {
      // Countable pressure event — deferrals replaced `saturated` skips as
      // the referendum on the bounded-gate prefetch design (decisions §4).
      logDetached(
        Effect.logInfo('Chunk fetch deferred').pipe(
          Effect.annotateLogs({ key, queue_depth: queueDepth }),
        ),
      );
    },
    onCircuitStateChange: (state) => {
      if (state === 'open') {
        // No eager fault: playback running from cache keeps playing (an
        // audio failure never pre-empts a working surface). The next
        // current-priority fetch probes or is skipped, and the skip handler
        // surfaces the fault at a moment when nothing is playing.
        logDetached(Effect.logWarning('Chunk-fetch circuit opened'));
        return;
      }
      store.setState({ playbackFault: null });
    },
  });
  const docPackChunkFetchKey = (params: DocPackTtsChunkParams) =>
    docPackChunkCacheKey({
      audioId: params.audioId,
      docPackVersion: params.docPackVersion,
      chunkId: params.chunk.id,
      voice: params.voice,
    });
  // The fetcher owns fetch policy end to end (typed errors, retry, circuit);
  // results land in the chunk-cache atom, which useCurrentAudioChunkQuery
  // reads to render the current chunk. TanStack holds no chunk data.
  const ensureLegacyChunkCached = (
    params: ChapterTtsChunkParams & { audioId: string },
  ): LegacyTtsChunk | undefined => {
    const key = legacyChunkCacheKey(params);
    const cached = readAudioChunkCache(registry, key);
    if (cached?.playbackMode === 'legacy') return cached;
    const persisted = read().readPersistedChunk?.(params);
    if (!persisted) return undefined;
    // Promote so UI reads (and later cache reads) see offline chunks too.
    const chunk = asLegacyTtsChunk(persisted);
    writeAudioChunkCache(registry, key, chunk);
    return chunk;
  };
  const readCachedDocPackChunk = (
    params: DocPackTtsChunkParams,
  ): DocPackTtsChunk | undefined => {
    const cached = readAudioChunkCache(registry, docPackChunkFetchKey(params));
    return cached?.playbackMode === 'doc-pack' ? cached : undefined;
  };
  const legacyChunkRequest = (params: {
    audioId: string;
    chapterId: string;
    ttsId: string;
    voice: string;
  }) => ({
    key: legacyChunkCacheKey(params),
    effect: getChapterTtsChunkEffect(params),
    readCached: () => ensureLegacyChunkCached(params),
    writeCached: (chunk: LegacyTtsChunk) => {
      writeAudioChunkCache(registry, legacyChunkCacheKey(params), chunk);
    },
  });
  const fetchLegacyChunkEffect = (
    params: {
      audioId: string;
      chapterId: string;
      ttsId: string;
      voice: string;
    },
    priority: ChunkFetchPriority,
  ) => chunkFetcher.fetchEffect(legacyChunkRequest(params), priority);
  const docPackChunkRequest = (params: DocPackTtsChunkParams) => ({
    key: docPackChunkFetchKey(params),
    effect: getDocPackTtsChunkEffect(params),
    readCached: () => readCachedDocPackChunk(params),
    writeCached: (chunk: DocPackTtsChunk) => {
      writeAudioChunkCache(registry, docPackChunkFetchKey(params), chunk);
    },
  });
  const fetchDocPackChunkEffect = (
    params: DocPackTtsChunkParams,
    priority: ChunkFetchPriority,
  ) => chunkFetcher.fetchEffect(docPackChunkRequest(params), priority);
  const getPlayer = () => read().player;
  /** The transport can legitimately refuse to start — a browser blocks
   * autoplay without a gesture — so a rejection is a fact about the session,
   * not a defect. Record it and let the store agree with reality: nothing is
   * playing until the user asks again. */
  const startPlayback = Effect.tryPromise({
    try: () => getPlayer().play(),
    catch: (error) =>
      new AudioPoolPlaybackError({
        code: 'play_rejected',
        message: error instanceof Error ? error.message : String(error),
      }),
  }).pipe(
    Effect.catchTag('AudioPoolPlaybackError', (error) =>
      Effect.logWarning('Transport refused to start playback').pipe(
        Effect.annotateLogs({ error }),
        Effect.andThen(
          Effect.sync(() => {
            store.setState({ autoPlay: false });
          }),
        ),
      ),
    ),
  );
  /** Interruption is supersession, not a chunk failure: it belongs to the
   * command fiber's finalizers and must never reach the recovery policy. */
  const isSupersession = (cause: Cause.Cause<unknown>) =>
    Cause.hasInterrupts(cause);

  /** The three records a chunk fetch emits, shared by the current-chunk load
   * and the prefetch passes so the two can never drift. */
  const ttsFetchLog = {
    started: (context: PlaybackChunkLogContext) => {
      logDetached(
        Effect.logInfo('TTS source fetch started').pipe(
          Effect.annotateLogs(playbackTtsFetchLogMetadata(context)),
        ),
      );
      return performance.now();
    },
    resolved: (
      context: PlaybackChunkLogContext,
      latencyMs: number,
      cacheHit: boolean,
    ) => {
      logDetached(
        Effect.logInfo('TTS source fetch finished').pipe(
          Effect.annotateLogs(
            playbackTtsFetchLogMetadata({
              ...context,
              latencyMs,
              cacheHit,
              resolved: true,
            }),
          ),
        ),
      );
    },
    failed: (
      context: PlaybackChunkLogContext,
      latencyMs: number,
      cacheHit: boolean,
      error: unknown,
    ) => {
      logDetached(
        Effect.logWarning('TTS source fetch finished').pipe(
          Effect.annotateLogs({
            ...playbackTtsFetchLogMetadata({
              ...context,
              latencyMs,
              cacheHit,
              resolved: false,
            }),
            error,
          }),
        ),
      );
    },
  };

  const ensureLoggedTtsFetchEffect = <T, E, R>({
    cacheHit,
    context,
    load,
    priority,
  }: {
    cacheHit: boolean;
    context: PlaybackChunkLogContext;
    load: Effect.Effect<T, E, R>;
    priority: ChunkFetchPriority;
  }) =>
    Effect.suspend(() => {
      const startedAtMs = ttsFetchLog.started(context);
      const sessionId = store.getState().stats.sessionId;
      const finish = (resolved: boolean, error?: unknown) => {
        const latencyMs = playbackTtsFetchElapsedMs(
          startedAtMs,
          performance.now(),
        );
        if (resolved) ttsFetchLog.resolved(context, latencyMs, cacheHit);
        else ttsFetchLog.failed(context, latencyMs, cacheHit, error);
        recordPlaybackStats(
          context.audioId,
          sessionId,
          recordFetch({ latencyMs, cacheHit, resolved, priority }),
        );
      };
      return load.pipe(
        Effect.tap(() => Effect.sync(() => finish(true))),
        Effect.tapCause((cause) =>
          isSupersession(cause)
            ? Effect.void
            : Effect.sync(() => finish(false, Cause.squash(cause))),
        ),
      );
    });

  /** Prefetch is best-effort. A skip is a countable policy event
   * (cooldown/gave-up/circuit — slot pressure surfaces separately as 'Chunk
   * fetch deferred'); any other failure is logged and dropped. */
  const prefetchOutcomeLogged =
    (playbackMode: 'legacy' | 'doc-pack', failMessage: string) =>
    (work: Effect.Effect<void, ChunkFetchError, AppServices>) =>
      work.pipe(
        Effect.catchTag('ChunkFetchSkippedError', (skip) =>
          Effect.logInfo('Chunk fetch skipped').pipe(
            Effect.annotateLogs({
              reason: skip.reason,
              key: skip.key,
              priority: 'prefetch',
              playback_mode: playbackMode,
            }),
          ),
        ),
        Effect.catchCause((cause) =>
          Effect.logWarning(failMessage).pipe(
            Effect.annotateLogs({ error: Cause.squash(cause) }),
          ),
        ),
      );
  const resolveLegacyAudioChunkSrc = async (params: {
    documentSummary: AudioItemDetail;
    audioChunk: ChapterTtsChunk;
  }) =>
    (await read().resolveAudioChunkSrc?.({
      audioId: params.documentSummary.id,
      chapterId: params.audioChunk.chapterId,
      ttsId: params.audioChunk.id,
      voice: params.audioChunk.voice,
      remoteUrl: params.audioChunk.url,
    })) ?? params.audioChunk.url;
  const getDocumentSummary = () => {
    const audioId = store.getState().audioId;
    if (!audioId) return;

    const audioItemDetail = getAudioDetailFromCache(queryClient, audioId) as
      | AudioItemDetail
      | undefined;
    return audioItemDetail;
  };

  const getVoice = () => {
    const audioId = store.getState().audioId;
    const preferredVoiceName = queryClient.getQueryData<UserPreferences>(
      userPreferencesQueryKey,
    )?.voice;
    const downloadedVoicesAtom = read().downloadedVoicesAtom;
    const audioPreferredVoiceName =
      audioId && downloadedVoicesAtom
        ? registry.get(downloadedVoicesAtom)[audioId]
        : undefined;
    const audioItemLang = (
      getAudioDetailFromCache(queryClient, audioId) as
        | AudioItemDetail
        | undefined
    )?.language;
    const isPremiumUser =
      readIsPremiumUserSeam(read().isPremiumUser) ??
      queryClient.getQueryData<GetSummaryResponse>(userSummaryQueryKey)
        ?.metering.is_active_subscription === true;
    const voiceOptionsAll =
      queryClient.getQueryData<Array<Voice>>(voicesQueryKey) ?? [];
    return resolveCurrentVoice({
      audioItemLang,
      audioPreferredVoiceName,
      preferredVoiceName,
      isPremiumUser,
      voiceOptionsAll,
    });
  };

  const getFreshDocPack = (documentSummary: AudioItemDetail) =>
    read().docPackEnabled && isDocPackVersionUsable(documentSummary.doc_pack)
      ? documentSummary.doc_pack
      : undefined;

  const getLoadedDocPack = (
    documentSummary: AudioItemDetail,
  ): Promise<LoadedDocPack | undefined> => {
    const docPack = getFreshDocPack(documentSummary);
    if (!docPack) return Promise.resolve(undefined);
    return queryClient.ensureQueryData(
      eq.queryOptions(docPackCatalogArgs(documentSummary.id, docPack.url)),
    );
  };

  const findDocPackChunkFrom = async (
    documentSummary: AudioItemDetail,
    from?: DocPackReadingAnchor,
  ): Promise<DocPackReadingChunk | undefined> => {
    const pack = await getLoadedDocPack(documentSummary);
    if (!pack) return;
    const classOverrides = resolveDocPackReadingClassOverrides(
      registry.get(docPackReadingPreferencesAtom),
      documentSummary.id,
    );
    const startPage = from?.page ?? pack.catalog.pages[0]?.n ?? 1;
    const anchor: DocPackReadingAnchor = from ?? {
      page: startPage,
      charStart: 0,
    };
    return findDocPackChunkByScan({
      catalog: pack.catalog,
      classOverrides,
      anchor,
      startPage,
      loadPage: (pageNumber) =>
        queryClient.ensureQueryData(
          eq.queryOptions(
            docPackPageArgs(documentSummary.id, pack, pageNumber, {
              fetchMode: read().docPackPageFetchMode,
            }),
          ),
        ),
      warmPages:
        read().docPackPageFetchMode === 'range'
          ? (pageNumbers) =>
              warmDocPackPagesCoalesced(
                runtime,
                queryClient,
                documentSummary.id,
                pack,
                pageNumbers,
              )
          : undefined,
    });
  };

  const lastDocPackAnchorForChunk = (
    chunk: DocPackReadingChunk,
  ): DocPackReadingAnchor => {
    const lastRange = assertDefined(chunk.ranges.at(-1));
    return {
      page: lastRange[0],
      charStart: lastRange[2],
    };
  };

  const docPackTtsParamsFor = (
    documentSummary: AudioItemDetail,
    chunk: DocPackReadingChunk,
  ): DocPackTtsChunkParams | undefined => {
    const docPack = getFreshDocPack(documentSummary);
    if (!docPack) return;
    return {
      audioId: documentSummary.id,
      docPackVersion: docPack.content_version,
      ranges: chunk.ranges,
      voice: getVoice().name,
      chunk,
    };
  };

  const ensureDocumentSummaryEffect = makeEnsureDocumentSummary({
    readSummary: getDocumentSummary,
    readAudioId: () => store.getState().audioId,
    fetchAudioDetail: (audioId) =>
      queryClient.ensureQueryData(eq.queryOptions(audioDetailArgs(audioId))),
    onAttemptFailed: ({ cause }, attempt) => {
      logDetached(
        Effect.logWarning(
          'ensureDocumentSummary: audio detail fetch failed',
        ).pipe(Effect.annotateLogs({ error: cause, attempt })),
      );
    },
  });

  const stopPlaybackOnMissingDetail = (message: string) => {
    logDetached(Effect.logError(message));
    store.setState({ isLoadingAudio: false, autoPlay: false });
  };

  // How deep the already-cached prefetch runway is, starting at the chunk
  // about to play.
  const cachedLookaheadDepth = (
    documentSummary: AudioItemDetail,
    firstChunk: { chapterId: string; ttsId: string },
  ) => {
    const voice = getVoice().name;
    let depth = 0;
    let cursor: { chapterId: string; ttsId: string } | undefined = firstChunk;
    while (cursor && depth < MAX_PREFETCH_CHUNKS) {
      const cached = ensureLegacyChunkCached({
        audioId: documentSummary.id,
        chapterId: cursor.chapterId,
        ttsId: cursor.ttsId,
        voice,
      });
      if (cached === undefined) break;
      depth += 1;
      cursor = findNextAudioChunk(
        documentSummary,
        cursor.chapterId,
        cursor.ttsId,
      );
    }
    return depth;
  };

  /** Advance failures are logged and swallowed, never faulted: a chunk that
   * will not load is the recovery policy's business, not the advance's. */
  const logAdvanceFailure = (message: string) =>
    Effect.tapCause((cause: Cause.Cause<unknown>) =>
      isSupersession(cause)
        ? Effect.void
        : Effect.sync(() => {
            logDetached(
              Effect.logError(message).pipe(
                Effect.annotateLogs({ error: Cause.squash(cause) }),
              ),
            );
          }),
    );

  /** How the two playback modes each find and select the chunk after the
   * current one. Everything else about an advance is shared. */
  type ChunkAdvancePlan = {
    readonly target: PlaybackTarget;
    readonly logMessage: string;
    /** `undefined` = end of document. Failing = the selection no longer
     * describes this document, which is NOT the end of it. */
    readonly findNext: (
      documentSummary: AudioItemDetail,
    ) => Effect.Effect<AdvanceSelection | undefined, StaleChunkSelection>;
  };
  type AdvanceSelection = {
    readonly lookaheadDepth: (documentSummary: AudioItemDetail) => number;
    readonly select: Partial<AudioChunkPlayerState>;
  };
  /** The store points at ids this document no longer has — a rechunked
   * conversion. Reported and dropped, never treated as completion. */
  class StaleChunkSelection {
    readonly _tag = 'StaleChunkSelection';
    constructor(readonly context: Record<string, unknown>) {}
  }

  const chunkAdvancePlan = (): ChunkAdvancePlan | undefined => {
    const { audioId, chapterId, ttsId, playbackMode, docPackChunk } =
      store.getState();
    if (playbackMode === 'doc-pack') {
      if (!(audioId && docPackChunk)) {
        logDetached(
          Effect.logWarning(
            'doc-pack audio ended but no audioId or docPackChunk',
          ),
        );
        return;
      }
      return {
        target: {
          playbackMode: 'doc-pack',
          audioId,
          docPackChunkId: docPackChunk.id,
        },
        logMessage: 'Error loading next doc-pack audio chunk',
        findNext: (documentSummary) =>
          Effect.promise(() =>
            findDocPackChunkFrom(
              documentSummary,
              lastDocPackAnchorForChunk(docPackChunk),
            ),
          ).pipe(
            Effect.map((nextChunk) =>
              nextChunk
                ? {
                    // Doc-pack prefetches a single chunk ahead: depth is 0/1.
                    lookaheadDepth: (documentSummary: AudioItemDetail) => {
                      const params = docPackTtsParamsFor(
                        documentSummary,
                        nextChunk,
                      );
                      return params &&
                        readCachedDocPackChunk(params) !== undefined
                        ? 1
                        : 0;
                    },
                    select: { docPackChunk: nextChunk },
                  }
                : undefined,
            ),
          ),
      };
    }
    if (!(audioId && chapterId && ttsId)) {
      logDetached(
        Effect.logWarning('Audio ended but no audioId, chapterId, or ttsId'),
      );
      return;
    }
    return {
      target: { playbackMode: 'legacy', audioId, chapterId, ttsId },
      logMessage: 'Error loading next audio chunk',
      findNext: (documentSummary) =>
        Effect.suspend(() => {
          if (
            isLegacyAudioChunkSelectionStale(documentSummary, chapterId, ttsId)
          ) {
            return Effect.fail(
              new StaleChunkSelection({
                chapterId,
                ttsId,
                audioId: documentSummary.id,
              }),
            );
          }
          const nextChunk = findNextAudioChunk(
            documentSummary,
            chapterId,
            ttsId,
          );
          return Effect.succeed(
            nextChunk
              ? {
                  lookaheadDepth: (documentSummary: AudioItemDetail) =>
                    cachedLookaheadDepth(documentSummary, nextChunk),
                  select: nextChunk,
                }
              : undefined,
          );
        }),
    };
  };

  const runAdvanceToNextChunkEffect = (
    command: Extract<PlaybackCommand, { _tag: 'advanceToNextChunk' }>,
  ) =>
    Effect.suspend(() => {
      const { autoPlay, onExhausted } = command;
      const plan = chunkAdvancePlan();
      if (!plan) return Effect.void;
      const isSameAdvanceTarget = () => isCurrentPlaybackTarget(plan.target);
      return Effect.gen(function* () {
        const documentSummary = yield* ensureDocumentSummaryEffect;
        if (!isSameAdvanceTarget()) return;
        if (!documentSummary) {
          stopPlaybackOnMissingDetail(
            'Audio ended but audio item detail not loaded',
          );
          return;
        }
        const next = yield* plan.findNext(documentSummary);
        if (!isSameAdvanceTarget()) return;
        if (!next) {
          // End of document.
          if (onExhausted === 'complete') boundaryMarker = null;
          store.setState({
            autoPlay: false,
            isLoadingAudio: false,
            ...(onExhausted === 'complete' ? { isPlaybackComplete: true } : {}),
          });
          return;
        }
        if (boundaryMarker) {
          boundaryMarker.lookaheadDepth = next.lookaheadDepth(documentSummary);
        }
        store.setState({
          ...next.select,
          loadPos: { posMs: 0 },
          autoPlay: autoPlay && store.getState().autoPlay,
          isPlaybackComplete: false,
        });
        // The store now points past the ended chunk. Recorded on the pending
        // record (identity-guarded: the recovery-tail advance inside a user
        // command is a different object) so a rerun after an interruption
        // finishes this chunk's load instead of advancing again.
        if (pendingAutomated?.dispatch.command === command) {
          pendingAutomated.advancedTo = playbackTargetOf(store.getState());
        }
        yield* loadAudioChunk(documentSummary, 'advance');
      }).pipe(
        Effect.catchTag('StaleChunkSelection', (stale) =>
          Effect.sync(() => {
            logDetached(
              Effect.logWarning(
                'Audio ended but the chunk selection is stale for this document',
              ).pipe(Effect.annotateLogs(stale.context)),
            );
          }),
        ),
        logAdvanceFailure(plan.logMessage),
        Effect.ignore,
      );
    });

  /** Finish the load an interrupted advance left behind: the store already
   * points at the advanced-to chunk (its `loadPos` intact — a failed or
   * interrupted attempt never consumes it), only the load is owed. */
  const runResumeAdvanceLoad = Effect.suspend(() =>
    Effect.gen(function* () {
      const documentSummary = yield* ensureDocumentSummaryEffect;
      if (!documentSummary) return;
      yield* loadAudioChunk(documentSummary, 'advance');
    }).pipe(
      logAdvanceFailure('Error resuming interrupted chunk advance'),
      Effect.ignore,
    ),
  );

  const audioEnded = (endedTarget: PlaybackTarget | undefined) => {
    perfMark('audio-chunk-player.audio-ended');
    logDetached(Effect.logInfo('Audio ended'));
    // Armed here so the gap is measured from the real end of audio; the
    // automated lane disarms it if the advance turns out not to belong to
    // what is playing.
    boundaryMarker = { startedAtMs: performance.now() };
    dispatchAutomatedCommand(
      { _tag: 'advanceToNextChunk', autoPlay: true, onExhausted: 'complete' },
      endedTarget,
    );
  };

  /** A transport failure raised once the chunk was already playable. The load
   * fiber settled at `onCanPlay`, so recovery is re-entered on the automated
   * lane — target-guarded, because this closure outlives its fiber.
   *
   * Deliberately NOT recorded as pending automated work: a user command that
   * interrupts this recovery replaces it, exactly as it cancels the in-fiber
   * retry of a load that failed before `onCanPlay`. */
  const chunkPlaybackFailed = (
    target: PlaybackTarget | undefined,
    error: AudioPoolPlaybackError,
  ) => {
    if (!target || !isCurrentPlaybackTarget(target)) return;
    registry.set(automatedCommandAtom, {
      command: {
        _tag: 'recoverPlaybackFailure',
        error,
        positionMs: Math.round(getPlayer().getPositionMs()),
      },
      target,
    });
  };

  /** Human-readable log line for a chunk failure. The strings are unchanged
   * from the six per-mode call sites they replace. */
  const chunkFailureLogMessage = (
    failure: ChunkLoadFailure,
    playbackMode: 'legacy' | 'doc-pack',
  ) => {
    const docPack = playbackMode === 'doc-pack';
    const fetchFailed = docPack
      ? 'doc-pack TTS source fetch failed'
      : 'TTS source fetch failed';
    switch (failure._tag) {
      case 'PlaybackLoadTimeoutError': {
        return docPack
          ? 'doc-pack audio load timed out'
          : 'Audio load timed out';
      }
      case 'AudioPoolPlaybackError': {
        return docPack ? 'doc-pack audio error' : 'Audio error';
      }
      case 'ChunkFetchSkippedError':
      case 'ChunkFetchFailedError': {
        return fetchFailed;
      }
      default: {
        failure satisfies never;
        return fetchFailed;
      }
    }
  };

  /**
   * Reports a chunk failure and carries out the recovery policy, in the fiber
   * that owns the load. `true` means "attempt the same chunk again".
   *
   * Retrying is a sleep in this fiber rather than a timer plus a fresh
   * command: a user command interrupts the fiber, which cancels the pending
   * retry, so a stale timer can no longer act on a session the user left.
   */
  const recoverFromChunkLoadFailure = (
    failure: ChunkLoadFailure,
    plan: ChunkLoadPlan,
  ) =>
    Effect.gen(function* () {
      if (failure._tag === 'ChunkFetchSkippedError') {
        // A skip is bookkeeping or scheduling, never evidence against the
        // chunk: countable, but it never touches the retry budget.
        yield* Effect.logInfo('Chunk fetch skipped').pipe(
          Effect.annotateLogs({
            reason: failure.reason,
            key: failure.key,
            priority: 'current',
            playback_mode: plan.logContext.playbackMode,
          }),
        );
      } else {
        yield* Effect.logError(
          chunkFailureLogMessage(failure, plan.logContext.playbackMode),
        ).pipe(Effect.annotateLogs({ error: failure }));
        // Fetch failures were already classified by the fetcher; a playback
        // failure is new transient evidence against the same chunk.
        if (countsAsNewChunkEvidence(failure)) {
          chunkFetcher.notePlaybackFailure(plan.key);
        }
        yield* Effect.logWarning('Playback recovery attempt').pipe(
          Effect.annotateLogs({
            ...playbackRecoveryAttemptLogMetadata({
              ...plan.logContext,
              attempt: chunkFetcher.failureCount(plan.key),
              reason: chunkLoadFailureReason(failure),
              positionMs: Math.round(getPlayer().getPositionMs()),
            }),
            error: failure,
          }),
        );
        recordPlaybackStats(
          plan.logContext.audioId,
          store.getState().stats.sessionId,
          recordRecovery(chunkLoadFailureReason(failure)),
        );
      }

      const action = resolveChunkLoadRecovery({
        failure,
        retryBudgetExhausted: chunkFetcher.retryBudgetExhausted(plan.key),
        incidentAdvanceCount,
      });
      switch (action.kind) {
        case 'ignore': {
          // Reaching this branch proves no newer command owns the load: a
          // superseded fiber is interrupted before it gets here. So the
          // spinner is ours to clear — nobody else will.
          store.setState({ isLoadingAudio: false });
          return false;
        }
        case 'fault': {
          store.setState({
            playbackFault: action.fault,
            isLoadingAudio: false,
            autoPlay: false,
          });
          return false;
        }
        case 'advance-past-broken-chunk': {
          incidentAdvanceCount += 1;
          store.setState({ isLoadingAudio: false });
          // The tail of the command that just failed, not a competing one:
          // it continues this fiber rather than dispatching against it.
          yield* runAdvanceToNextChunkEffect({
            _tag: 'advanceToNextChunk',
            autoPlay: store.getState().autoPlay,
            onExhausted: 'stop',
          });
          return false;
        }
        case 'retry-same-chunk': {
          store.setState({ isLoadingAudio: true });
          yield* Effect.sleep(Duration.millis(action.delayMs));
          return true;
        }
        // no default
      }
    });

  const prefetchNextChunks = (documentSummary: AudioItemDetail) => {
    const { chapterId, ttsId, audioId, playbackMode, docPackChunk } =
      store.getState();
    if (audioId !== documentSummary.id) {
      logDetached(
        Effect.logWarning(
          'Prefetching next chunks but audioId does not match document summary',
        ),
      );
      return;
    }
    if (playbackMode === 'doc-pack') {
      if (!docPackChunk) {
        logDetached(
          Effect.logWarning(
            'Prefetching next doc-pack chunk but no docPackChunk',
          ),
        );
        return;
      }
      forkDetached(
        Effect.gen(function* () {
          const nextChunk = yield* Effect.promise(() =>
            findDocPackChunkFrom(
              documentSummary,
              lastDocPackAnchorForChunk(docPackChunk),
            ),
          );
          if (!nextChunk) return;
          const params = docPackTtsParamsFor(documentSummary, nextChunk);
          if (!params) return;
          const context = {
            audioId: params.audioId,
            chapterId: docPackChapterId(params.audioId),
            ttsId: params.chunk.id,
            playbackMode: 'doc-pack' as const,
            docPackChunkId: params.chunk.id,
          };
          const audioChunk = yield* ensureLoggedTtsFetchEffect({
            cacheHit: readCachedDocPackChunk(params) !== undefined,
            context,
            load: fetchDocPackChunkEffect(params, 'prefetch'),
            priority: 'prefetch',
          });
          setTtsChunkAudioDurationMs(
            registry,
            audioChunk.docPackChunk.id,
            audioChunk.voice,
            audioChunk.duration_ms,
          );
          getPlayer().prewarmLoad(audioChunk.url, {
            playbackLogMetadata: context,
          });
        }).pipe(
          prefetchOutcomeLogged(
            'doc-pack',
            'Failed to prefetch next doc-pack chunk',
          ),
        ),
      );
      return;
    }
    if (!(chapterId && ttsId)) {
      logDetached(
        Effect.logWarning('Prefetching next chunks but no chapterId or ttsId'),
      );
      return;
    }

    const prefetchNextChunkAndReturnNextChunk = (
      currentChunk: {
        chapterId: string;
        ttsId: string;
      },
      warmPlayer: boolean,
    ) => {
      const nextChunk = findNextAudioChunk(
        documentSummary,
        currentChunk.chapterId,
        currentChunk.ttsId,
      );
      if (nextChunk) {
        const params = {
          audioId: documentSummary.id,
          chapterId: nextChunk.chapterId,
          ttsId: nextChunk.ttsId,
          voice: getVoice().name,
        };
        forkDetached(
          Effect.gen(function* () {
            const audioChunk = yield* ensureLoggedTtsFetchEffect({
              cacheHit: ensureLegacyChunkCached(params) !== undefined,
              context: {
                audioId: documentSummary.id,
                chapterId: params.chapterId,
                ttsId: params.ttsId,
                playbackMode: 'legacy',
              },
              load: fetchLegacyChunkEffect(params, 'prefetch'),
              priority: 'prefetch',
            });
            setTtsChunkAudioDurationMs(
              registry,
              audioChunk.id,
              audioChunk.voice,
              audioChunk.duration_ms,
            );
            if (!warmPlayer) return;
            const src = yield* Effect.promise(() =>
              resolveLegacyAudioChunkSrc({ documentSummary, audioChunk }),
            );
            getPlayer().prewarmLoad(src, {
              playbackLogMetadata: {
                audioId: documentSummary.id,
                chapterId: audioChunk.chapterId,
                chapterTitle: chapterTitleForId(
                  documentSummary,
                  audioChunk.chapterId,
                ),
                ttsId: audioChunk.id,
                playbackMode: 'legacy',
              },
            });
          }).pipe(
            prefetchOutcomeLogged(
              'legacy',
              'Failed to prefetch next audio chunk',
            ),
          ),
        );
      }
      return nextChunk;
    };

    const playbackRate = registry.get(audioPreferencesAtom).playbackRate;
    const numberOfChunksToPrefetch = clamp(
      Math.round(playbackRate * 3) + 1,
      5,
      MAX_PREFETCH_CHUNKS,
    );
    let currentChunk: ReturnType<typeof prefetchNextChunkAndReturnNextChunk> = {
      chapterId,
      ttsId,
    };

    logDetached(
      Effect.logInfo(
        `Prefetching next ${numberOfChunksToPrefetch.toString()} chunks`,
      ),
    );
    const numberOfChunksToWarm = decodedPrefetchWarmCount({
      poolSize: getPlayer().poolSize,
      prefetchCount: numberOfChunksToPrefetch,
    });
    for (let i = 0; i < numberOfChunksToPrefetch; i++) {
      if (!currentChunk) break;
      currentChunk = prefetchNextChunkAndReturnNextChunk(
        currentChunk,
        i < numberOfChunksToWarm,
      );
    }
  };

  /**
   * Everything that differs between a legacy and a doc-pack load, resolved
   * once per load. The load body reads only this, so the two modes share one
   * implementation instead of two near-identical ones.
   */
  type ChunkLoadPlan = {
    readonly key: string;
    readonly logContext: PlaybackChunkLogContext;
    readonly isCached: () => boolean;
    readonly fetch: Effect.Effect<PlaybackChunk, ChunkFetchError, AppServices>;
    /** The selection this plan was built for is still the one in the store. */
    readonly stillSelected: (chunk: PlaybackChunk) => boolean;
    readonly durationKey: (chunk: PlaybackChunk) => string;
    readonly resolveSrc: (chunk: PlaybackChunk) => Effect.Effect<string>;
    readonly playbackLogMetadata: AudioPoolPlaybackLogMetadata;
    /** The chunk this load is for. Callbacks the transport keeps — `onEnded`
     * above all — carry it, so the work they trigger can tell whether it still
     * belongs to what is playing. */
    readonly target: PlaybackTarget | undefined;
  };

  const chunkLoadPlan = (documentSummary: AudioItemDetail) =>
    Effect.gen(function* () {
      const { chapterId, ttsId, playbackMode, docPackChunk } = store.getState();
      if (playbackMode === 'doc-pack') {
        if (!docPackChunk)
          return yield* new PlaybackSelectionInvariantError({
            message: 'doc-pack playback with no doc-pack chunk selected',
          });
        const params = docPackTtsParamsFor(documentSummary, docPackChunk);
        if (!params)
          return yield* new PlaybackSelectionInvariantError({
            message: 'doc-pack playback with no decodable doc-pack',
          });
        const logContext = {
          audioId: params.audioId,
          chapterId: docPackChapterId(params.audioId),
          ttsId: params.chunk.id,
          playbackMode: 'doc-pack' as const,
          docPackChunkId: params.chunk.id,
        };
        return {
          key: docPackChunkFetchKey(params),
          logContext,
          isCached: () => readCachedDocPackChunk(params) !== undefined,
          fetch: fetchDocPackChunkEffect(params, 'current'),
          stillSelected: (chunk) =>
            chunk.playbackMode === 'doc-pack' &&
            chunk.docPackChunk.id === store.getState().docPackChunk?.id,
          durationKey: (chunk) =>
            chunk.playbackMode === 'doc-pack'
              ? chunk.docPackChunk.id
              : chunk.id,
          resolveSrc: (chunk) => Effect.succeed(chunk.url),
          playbackLogMetadata: logContext,
          target: playbackTargetOf(store.getState()),
        } satisfies ChunkLoadPlan;
      }
      if (!(chapterId && ttsId))
        return yield* new PlaybackSelectionInvariantError({
          message: 'legacy playback with no chapterId or ttsId selected',
        });
      const params = {
        audioId: documentSummary.id,
        chapterId,
        ttsId,
        voice: getVoice().name,
      };
      const logContext = {
        audioId: documentSummary.id,
        chapterId,
        ttsId,
        playbackMode: 'legacy' as const,
      };
      return {
        key: legacyChunkCacheKey(params),
        logContext,
        isCached: () => ensureLegacyChunkCached(params) !== undefined,
        fetch: fetchLegacyChunkEffect(params, 'current'),
        stillSelected: (chunk) => chunk.id === store.getState().ttsId,
        durationKey: (chunk) => chunk.id,
        resolveSrc: (chunk) =>
          Effect.promise(() =>
            resolveLegacyAudioChunkSrc({
              documentSummary,
              audioChunk: chunk,
            }),
          ),
        playbackLogMetadata: {
          ...logContext,
          chapterTitle: chapterTitleForId(documentSummary, chapterId),
        },
        target: playbackTargetOf(store.getState()),
      } satisfies ChunkLoadPlan;
    });

  /** Where in the chunk this load should start. `undefined` means "wherever
   * the transport already is" — the top, for a fresh load. */
  const seekSecondsForLoadPos = (
    chunk: PlaybackChunk,
    loadPos: LoadPos,
  ): number | undefined => {
    if ('posMs' in loadPos)
      return loadPos.posMs === 0 ? undefined : loadPos.posMs / 1000;
    if ('wordIdxInChunk' in loadPos) {
      const timestamp = chunk.alignment_timestamps[loadPos.wordIdxInChunk];
      return timestamp ? timestamp.start / 1000 : undefined;
    }
    if (chunk.playbackMode !== 'doc-pack') return undefined;
    const timing = chunk.docPackWordTimings.find(
      (candidate) =>
        candidate.page === loadPos.docPackPage &&
        candidate.char_start <= loadPos.docPackCharStart &&
        loadPos.docPackCharStart < candidate.char_end,
    );
    return timing ? timing.start_ms / 1000 : undefined;
  };

  /** One attempt: fetch the chunk, hand it to the transport, and wait for it
   * to become playable. Every way that can fail is a `ChunkLoadFailure` the
   * recovery policy reads. */
  const runChunkLoadAttempt = (
    plan: ChunkLoadPlan,
    documentSummary: AudioItemDetail,
    cause?: 'advance',
  ) =>
    Effect.gen(function* () {
      perfMark('audio-chunk-player.load-audio-chunk');
      yield* Effect.logInfo('Load Audio Chunk');
      store.setState({ isLoadingAudio: true, isPlaybackComplete: false });

      const audioChunk = yield* ensureLoggedTtsFetchEffect({
        cacheHit: plan.isCached(),
        context: plan.logContext,
        load: plan.fetch,
        priority: 'current',
      });

      // Interruption already covers a superseding command; this holds the
      // invariant that nothing else re-points the store mid-load.
      if (!plan.stillSelected(audioChunk)) {
        yield* Effect.logWarning(
          'Audio chunk changed before we could fetch. Returning early',
        );
        store.setState({ isLoadingAudio: false });
        return;
      }

      setTtsChunkAudioDurationMs(
        registry,
        plan.durationKey(audioChunk),
        audioChunk.voice,
        audioChunk.duration_ms,
      );

      const src = yield* plan.resolveSrc(audioChunk);
      const { loadPos, autoPlay } = store.getState();
      const preferences = registry.get(audioPreferencesAtom);
      const startedAtMs = performance.now();

      yield* loadChunkOnPlayer({
        player: getPlayer(),
        src,
        timeoutMs: PLAYBACK_LOAD_TIMEOUT_MS,
        options: {
          autoplay: autoPlay,
          cause,
          volume: resolveAudioVolume(preferences),
          rate: preferences.playbackRate,
          metadata: buildNowPlayingMetadata(
            documentSummary,
            store.getState().chapterId,
          ),
          playbackLogMetadata: plan.playbackLogMetadata,
          onEnded: () => {
            audioEnded(plan.target);
          },
        },
        onPlaybackError: (error) => {
          chunkPlaybackFailed(plan.target, error);
        },
      });

      // Playable.
      incidentAdvanceCount = 0;
      chunkFetcher.notePlaybackSuccess(plan.key);
      if (boundaryMarker) {
        const gapMs = performance.now() - boundaryMarker.startedAtMs;
        yield* Effect.logInfo('Playback boundary').pipe(
          Effect.annotateLogs(
            playbackBoundaryLogMetadata({
              audioId: plan.logContext.audioId,
              playbackMode: plan.logContext.playbackMode,
              ttsId: plan.logContext.ttsId,
              ...(plan.logContext.docPackChunkId
                ? { docPackChunkId: plan.logContext.docPackChunkId }
                : {}),
              gapMs,
              lookaheadDepth: boundaryMarker.lookaheadDepth,
            }),
          ),
        );
        recordPlaybackStats(
          plan.logContext.audioId,
          store.getState().stats.sessionId,
          recordBoundary({
            gapMs,
            lookaheadDepth: boundaryMarker.lookaheadDepth ?? null,
          }),
        );
        boundaryMarker = null;
      }
      store.setState({ isLoadingAudio: false, playbackFault: null });
      yield* Effect.logDebug(
        `player.load() took ${(performance.now() - startedAtMs).toFixed(3)}ms`,
      ).pipe(
        Effect.annotateLogs({
          ...plan.logContext,
          loadPos,
          autoPlay,
          src: src.slice(0, 30),
        }),
      );

      const seekSeconds = seekSecondsForLoadPos(audioChunk, loadPos);
      if (seekSeconds !== undefined) getPlayer().seek(seekSeconds);
      // Fire and forget: the chunk is playable, and the rest of this load
      // must not wait on the transport's start.
      if (!autoPlay && store.getState().autoPlay) forkDetached(startPlayback);
      // Consumed. A FAILED attempt keeps it, so a silent retry still lands
      // where the user asked rather than at the top of the chunk.
      store.setState({ loadPos: { posMs: 0 } });

      forkDetached(
        Effect.sleep(Duration.millis(read().delayToPrefetchMs ?? 200)).pipe(
          Effect.andThen(
            Effect.sync(() => {
              prefetchNextChunks(documentSummary);
            }),
          ),
          Effect.tapCause((cause) =>
            Effect.logWarning('Failed to schedule chunk prefetch').pipe(
              Effect.annotateLogs({ error: Cause.squash(cause) }),
            ),
          ),
          Effect.ignore,
        ),
      );
    });

  const attemptChunkLoadWithRecovery = (
    plan: ChunkLoadPlan,
    documentSummary: AudioItemDetail,
    cause?: 'advance',
  ): Effect.Effect<void, PlaybackCommandError, AppServices> =>
    runChunkLoadAttempt(plan, documentSummary, cause).pipe(
      Effect.catchTag(chunkLoadFailureTags, (failure) =>
        recoverFromChunkLoadFailure(failure, plan).pipe(
          Effect.flatMap((again) =>
            again
              ? attemptChunkLoadWithRecovery(plan, documentSummary, cause)
              : Effect.void,
          ),
        ),
      ),
    );

  /**
   * The ONLY entry point to a chunk load, and the only place that guarantees
   * `isLoadingAudio` is released.
   *
   * Recovery loops in this fiber rather than dispatching a fresh command: the
   * retry belongs to the load that failed, and staying here is what lets a
   * superseding command cancel it.
   *
   * A superseded load must NOT clear the flag — the load that replaced it
   * owns it now — which is why the release skips interruption.
   *
   * Fault-clearing and the immediate circuit probe live in
   * `beginPlaybackCommand` (user intent), never here: an automated retry must
   * not masquerade as a user probe or clear a newer session's fault.
   */
  const loadAudioChunk = (
    documentSummary: AudioItemDetail,
    cause?: 'advance',
  ): Effect.Effect<void, PlaybackCommandError, AppServices> =>
    Effect.gen(function* () {
      if (documentSummary.id !== store.getState().audioId) {
        yield* Effect.logWarning(
          'Loading audio chunk but audioId does not match document summary',
        );
        return;
      }
      if (read().userLoggedIn)
        yield* Effect.promise(() =>
          queryClient.ensureQueryData(eq.queryOptions(userPreferencesArgs)),
        );
      const plan = yield* chunkLoadPlan(documentSummary);
      yield* attemptChunkLoadWithRecovery(plan, documentSummary, cause);
    }).pipe(
      Effect.tapCause((failureCause) =>
        isSupersession(failureCause)
          ? Effect.void
          : Effect.sync(() => {
              store.setState({ isLoadingAudio: false });
            }),
      ),
    );

  /** The recovery tail of a load that already succeeded, run as its own
   * command because the fiber that owned the load is gone. Same policy, same
   * retry budget, same silent retry as a failure caught inside the load. */
  const runRecoverPlaybackFailureEffect = ({
    error,
    positionMs,
  }: Extract<PlaybackCommand, { _tag: 'recoverPlaybackFailure' }>) =>
    Effect.gen(function* () {
      const documentSummary = yield* ensureDocumentSummaryEffect;
      if (!documentSummary) {
        stopPlaybackOnMissingDetail(
          'Audio failed mid-chunk but audio item detail not loaded',
        );
        return;
      }
      const plan = yield* chunkLoadPlan(documentSummary);
      const again = yield* recoverFromChunkLoadFailure(error, plan);
      if (!again) return;
      // Set here, not at dispatch: an interrupted recovery must leave the
      // store's own load position alone.
      if (positionMs > 0) store.setState({ loadPos: { posMs: positionMs } });
      yield* loadAudioChunk(documentSummary);
    });

  const runSkipToChunkEffect = ({
    ttsId,
    loadParams,
  }: Extract<PlaybackCommand, { _tag: 'skipToChunk' }>) =>
    Effect.gen(function* () {
      beginPlaybackCommand();
      yield* Effect.logInfo('Skip to Chunk').pipe(
        Effect.annotateLogs({ ttsId, loadParams }),
      );
      if (!store.getState().audioId)
        return yield* new NoLoadedAudioError({
          message: 'Skip to chunk with no loaded audio',
        });

      const documentSummary = getDocumentSummary();
      if (!documentSummary) {
        yield* Effect.logWarning(
          'Skip to chunk called but audio item detail not loaded',
        );
        return;
      }
      const chapter = documentSummary.chapters.find((c) =>
        c.tts_ids.includes(ttsId),
      );
      if (!chapter)
        return yield* new NoPlayableChunkError({
          message: `No chapter contains TTS chunk ${ttsId}`,
        });

      if (getFreshDocPack(documentSummary)) {
        const docPackAnchor = docPackAnchorForLegacyTtsChunk(
          documentSummary,
          ttsId,
        );
        if (docPackAnchor) {
          const selectedDocPackChunk = yield* Effect.promise(() =>
            findDocPackChunkFrom(documentSummary, docPackAnchor),
          );
          if (selectedDocPackChunk) {
            getPlayer().pause();
            store.setState({
              playbackMode: 'doc-pack',
              docPackChunk: selectedDocPackChunk,
              chapterId: undefined,
              ttsId: undefined,
              loadPos: {
                docPackPage: docPackAnchor.page,
                docPackCharStart: docPackAnchor.charStart,
              },
              autoPlay: loadParams?.autoPlay ?? true,
              isLoadingAudio: true,
              isPlaybackComplete: false,
            });
            yield* loadAudioChunk(documentSummary);
            return;
          }
        }
        yield* Effect.logWarning(
          'Fresh doc-pack was available but legacy chunk could not bridge to a readable doc-pack chunk; falling back to legacy audio',
        ).pipe(Effect.annotateLogs({ ttsId }));
      }

      getPlayer().pause();
      store.setState({
        playbackMode: 'legacy',
        docPackChunk: undefined,
        ttsId,
        chapterId: chapter.id,
        loadPos: loadParams?.loadPos ?? { posMs: 0 },
        autoPlay: loadParams?.autoPlay ?? true,
        isLoadingAudio: true,
        isPlaybackComplete: false,
      });
      yield* loadAudioChunk(documentSummary);
    });

  const skipToChunk: AudioChunkPlayerActions['skipToChunk'] = (
    ttsId,
    loadParams,
  ) => {
    dispatchPlaybackCommand({ _tag: 'skipToChunk', ttsId, loadParams });
  };

  const runSkipToDocPackRangeEffect = ({
    anchor,
    loadParams,
  }: Extract<PlaybackCommand, { _tag: 'skipToDocPackRange' }>) =>
    Effect.gen(function* () {
      beginPlaybackCommand();
      yield* Effect.logInfo('Skip to doc-pack Range').pipe(
        Effect.annotateLogs({ anchor, loadParams }),
      );
      if (!store.getState().audioId)
        return yield* new NoLoadedAudioError({
          message: 'Skip to doc-pack range with no loaded audio',
        });

      const documentSummary = getDocumentSummary();
      if (!documentSummary) {
        yield* Effect.logWarning(
          'Skip to doc-pack range called but audio item detail not loaded',
        );
        return;
      }

      const selectedDocPackChunk = yield* Effect.promise(() =>
        findDocPackChunkFrom(documentSummary, anchor),
      );
      if (!selectedDocPackChunk)
        return yield* new NoPlayableChunkError({
          message: `No readable doc-pack chunk at page ${anchor.page.toString()}`,
        });

      getPlayer().pause();
      store.setState({
        playbackMode: 'doc-pack',
        docPackChunk: selectedDocPackChunk,
        chapterId: undefined,
        ttsId: undefined,
        loadPos: loadParams?.loadPos ?? {
          docPackPage: anchor.page,
          docPackCharStart: anchor.charStart,
        },
        autoPlay: loadParams?.autoPlay ?? true,
        isLoadingAudio: true,
        isPlaybackComplete: false,
      });
      yield* loadAudioChunk(documentSummary);
    });

  const skipToDocPackRange: AudioChunkPlayerActions['skipToDocPackRange'] = (
    anchor,
    loadParams,
  ) => {
    dispatchPlaybackCommand({
      _tag: 'skipToDocPackRange',
      anchor,
      loadParams,
    });
  };

  const runLoadAudioEffect = ({
    audioId,
    chapterId: requestedChapterId,
    ttsId: requestedTtsId,
    loadParams: requestedLoadParams,
    noteTarget,
  }: Extract<PlaybackCommand, { _tag: 'loadAudio' }>) =>
    Effect.gen(function* () {
      // Captured before beginPlaybackCommand clears it: a faulted same item
      // must resolve to a fresh load, not resume a stale queue.
      const hadPlaybackFault = store.getState().playbackFault !== null;
      beginPlaybackCommand();
      const currentState = store.getState();
      const shouldPlay = requestedLoadParams?.autoPlay ?? true;
      const isSameItem =
        currentState.audioId === audioId ||
        currentState.lookupAudioId === audioId;
      const currentDocumentSummary = getDocumentSummary();
      const isCurrentLegacyTargetStale =
        currentState.playbackMode === 'legacy' &&
        currentDocumentSummary !== undefined &&
        isLegacyAudioChunkSelectionStale(
          currentDocumentSummary,
          currentState.chapterId,
          currentState.ttsId,
        );
      // A note target retargets the session even when this document is already
      // playing, so it never resumes the loaded queue: reader-to-reader
      // navigation with a new `?note=` arrives as a same-item load.
      if (isSameItem && !isCurrentLegacyTargetStale && !noteTarget) {
        const player = getPlayer();
        const nextAction = resolveSameItemPlaybackAction({
          shouldPlay,
          forcePlay: currentState.isPlaybackComplete,
          hasPlaybackFault: hadPlaybackFault,
          isPlaying: player.isPlaying,
          hasAudioSource:
            player.src.length > 0 ||
            currentState.ttsId !== undefined ||
            currentState.docPackChunk !== undefined,
          hasLoadedAudio: currentDocumentSummary !== undefined,
          hasLoadedChunk:
            currentState.ttsId !== undefined ||
            currentState.docPackChunk !== undefined ||
            currentState.isLoadingAudio,
        });

        if (nextAction === 'no-op') {
          return;
        }

        if (nextAction === 'play-existing-queue') {
          store.setState({
            autoPlay: true,
            isLoadingAudio: false,
            isPlaybackComplete: false,
          });
          yield* startPlayback;
          return;
        }
      }

      getPlayer().stop();
      // A fresh load boundary: per-key failure state from the previous
      // document session is stale evidence (and would otherwise accumulate
      // across documents).
      chunkFetcher.reset();
      yield* Effect.logInfo('Load Audio').pipe(
        Effect.annotateLogs({
          audioId,
          chapterId: requestedChapterId,
          ttsId: requestedTtsId,
          loadParams: requestedLoadParams,
          noteTarget,
        }),
      );
      store.setState({
        audioId,
        lookupAudioId: audioId,
        chapterId: undefined,
        ttsId: undefined,
        playbackMode: 'legacy',
        docPackChunk: undefined,
        loadPos: noteTarget?.loadParams.loadPos ??
          requestedLoadParams?.loadPos ?? { posMs: 0 },
        autoPlay: requestedLoadParams?.autoPlay ?? true,
        isLoadingAudio: true,
        isPlaybackComplete: false,
        stats: resetPlaybackSessionStats(store.getState().stats),
      });
      // The first chunk key is derived from the voice, and the voice from the
      // entitlement, so a load that outran the app's answer would fetch the
      // free fallback voice for a premium user. Waited alongside the detail
      // fetch so it costs nothing when the answer is already in.
      const [audioItemDetail] = yield* Effect.all(
        [
          Effect.tryPromise({
            try: () =>
              queryClient.ensureQueryData(
                eq.queryOptions(audioDetailArgs(audioId)),
              ),
            catch: (cause) => new AudioDetailFetchError({ audioId, cause }),
          }),
          Effect.promise(
            () => read().awaitEntitlementReady?.() ?? Promise.resolve(),
          ),
        ],
        { concurrency: 2 },
      );

      const canonicalId = audioItemDetail.id;
      if (canonicalId !== audioId) {
        yield* Effect.logDebug(
          'Normalized store audioId to canonical UUID',
        ).pipe(Effect.annotateLogs({ lookupId: audioId, canonicalId }));
        store.setState({ audioId: canonicalId });
        queryClient.setQueryData(
          audioDetailArgs(canonicalId).queryKey,
          audioItemDetail,
        );
      }

      if (!isAudioDetailReadyForStreaming(audioItemDetail)) {
        yield* Effect.logWarning(
          'Audio detail is not ready for streaming',
        ).pipe(
          Effect.annotateLogs({
            audioId: audioItemDetail.id,
            conversionStatus: audioItemDetail.conversion_status,
          }),
        );
        store.setState({
          isLoadingAudio: false,
          autoPlay: false,
          isPlaybackComplete: false,
        });
        return;
      }

      const documentSummary = audioItemDetail;
      const appliedNoteTarget =
        noteTarget?.audioConversionId === canonicalId ? noteTarget : undefined;
      if (noteTarget && !appliedNoteTarget) {
        yield* Effect.logWarning(
          'Note belongs to another document; loading saved progress instead',
        ).pipe(
          Effect.annotateLogs({
            audioId: canonicalId,
            noteAudioId: noteTarget.audioConversionId,
          }),
        );
      }
      const { chapterId, ttsId, loadParams } = appliedNoteTarget
        ? noteTargetLoadArgs(appliedNoteTarget)
        : noteTarget
          ? audioProgressLoadArgs(documentSummary.progress)
          : {
              chapterId: requestedChapterId,
              ttsId: requestedTtsId,
              loadParams: requestedLoadParams,
            };
      const chapter = documentSummary.chapters.find((candidate) =>
        chapterId
          ? candidate.id === chapterId
          : ttsId !== undefined && candidate.tts_ids.includes(ttsId),
      );

      if (getFreshDocPack(documentSummary)) {
        const docPackFrom =
          loadParams?.docPackFrom ??
          docPackAnchorForLegacyChapterStart(documentSummary, chapterId) ??
          docPackAnchorForLegacyTtsChunk(documentSummary, ttsId);
        const selectedDocPackChunk = yield* Effect.promise(() =>
          findDocPackChunkFrom(documentSummary, docPackFrom),
        );
        if (selectedDocPackChunk) {
          store.setState({
            playbackMode: 'doc-pack',
            chapterId: undefined,
            ttsId: undefined,
            docPackChunk: selectedDocPackChunk,
            loadPos: docPackFrom
              ? {
                  docPackPage: docPackFrom.page,
                  docPackCharStart: docPackFrom.charStart,
                }
              : (loadParams?.loadPos ?? { posMs: 0 }),
          });
          yield* loadAudioChunk(documentSummary);
          return;
        }
        yield* Effect.logWarning(
          'Fresh doc-pack was available but no readable doc-pack chunk was found; falling back to legacy audio',
        );
      }

      if (chapterId && !chapter) {
        yield* Effect.logWarning(
          'Saved chapter progress is stale; falling back to first playable chunk',
        ).pipe(Effect.annotateLogs({ chapterId, audioId: documentSummary.id }));
      } else if (ttsId && !chapter?.tts_ids.includes(ttsId)) {
        yield* Effect.logWarning(
          'Saved TTS progress is stale; falling back to first playable chunk',
        ).pipe(
          Effect.annotateLogs({
            chapterId,
            ttsId,
            audioId: documentSummary.id,
          }),
        );
      }

      const selectedAudioChunk = resolveLegacyAudioChunkSelection(
        documentSummary,
        chapterId,
        ttsId,
      );

      if (!selectedAudioChunk)
        return yield* new NoPlayableChunkError({
          message: `Document ${documentSummary.id} has no playable TTS chunk`,
        });

      store.setState({
        playbackMode: 'legacy',
        docPackChunk: undefined,
        chapterId: selectedAudioChunk.chapterId,
        ttsId: selectedAudioChunk.ttsId,
        loadPos: didLegacyAudioChunkSelectionFallback(
          selectedAudioChunk,
          chapterId,
          ttsId,
        )
          ? { posMs: 0 }
          : (loadParams?.loadPos ?? { posMs: 0 }),
      });

      yield* loadAudioChunk(documentSummary);
    });

  const runPlaybackCommand = (command: PlaybackCommand) => {
    switch (command._tag) {
      case 'loadAudio': {
        return runLoadAudioEffect(command);
      }
      case 'skipToChunk': {
        return runSkipToChunkEffect(command);
      }
      case 'skipToDocPackRange': {
        return runSkipToDocPackRangeEffect(command);
      }
      case 'reloadChunk': {
        return runReloadChunkEffect();
      }
      case 'recoverPlaybackFailure': {
        return runRecoverPlaybackFailureEffect(command);
      }
      case 'advanceToNextChunk': {
        return runAdvanceToNextChunkEffect(command);
      }
      // no default
    }
  };

  const loadAudio: AudioChunkPlayerActions['loadAudio'] = (
    audioId,
    chapterId,
    ttsId,
    loadParams,
  ) => {
    dispatchPlaybackCommand({
      _tag: 'loadAudio',
      audioId,
      chapterId,
      ttsId,
      loadParams,
    });
  };

  const runReloadChunkEffect = () =>
    Effect.gen(function* () {
      beginPlaybackCommand();
      yield* Effect.logInfo('Reload Chunk');
      const documentSummary = getDocumentSummary();
      if (!documentSummary) {
        yield* Effect.logWarning(
          'Reload chunk called but document summary not loaded',
        );
        return;
      }
      const { playbackMode, docPackChunk } = store.getState();
      getPlayer().pause();
      if (playbackMode === 'doc-pack') {
        if (!docPackChunk) {
          yield* Effect.logWarning(
            'Reload doc-pack chunk called but no doc-pack chunk loaded',
          );
          return;
        }

        const params = docPackTtsParamsFor(documentSummary, docPackChunk);
        const currentAudioChunk = params
          ? readCachedDocPackChunk(params)
          : undefined;
        const currentAnchor = getDocPackPlaybackAnchorForPosition(
          currentAudioChunk,
          getPlayer().getPositionMs(),
        ) ?? {
          page: assertDefined(docPackChunk.ranges[0])[0],
          charStart: assertDefined(docPackChunk.ranges[0])[1],
        };
        const selectedDocPackChunk = yield* Effect.promise(() =>
          findDocPackChunkFrom(documentSummary, currentAnchor),
        );
        if (!selectedDocPackChunk) {
          yield* Effect.logWarning(
            'Reload doc-pack chunk found no readable chunk from current anchor',
          ).pipe(Effect.annotateLogs({ currentAnchor }));
          store.setState({ isLoadingAudio: false });
          return;
        }

        store.setState({
          playbackMode: 'doc-pack',
          docPackChunk: selectedDocPackChunk,
          chapterId: undefined,
          ttsId: undefined,
          loadPos: {
            docPackPage: currentAnchor.page,
            docPackCharStart: currentAnchor.charStart,
          },
          autoPlay: true,
          isLoadingAudio: true,
          isPlaybackComplete: false,
        });
        yield* loadAudioChunk(documentSummary);
        return;
      }

      store.setState({
        loadPos: { posMs: getPlayer().getPositionMs() },
        autoPlay: true,
        isPlaybackComplete: false,
      });
      yield* loadAudioChunk(documentSummary);
    });

  const reloadChunk: AudioChunkPlayerActions['reloadChunk'] = () => {
    dispatchPlaybackCommand({ _tag: 'reloadChunk' });
  };

  // User transport intent. UI must pause/play through these, not through the
  // pool player directly: they record the intent in store.autoPlay, which the
  // recovery paths (silent retry after a load timeout, chunk-advance after an
  // audio error) consult before resuming — a direct getPlayer().pause() leaves
  // autoPlay=true and a later recovery would un-pause the user.
  //
  // Imperative actions, NOT commands, by design: a command dispatch interrupts
  // whatever is running, and play/pause retarget nothing — a play tap during
  // an in-flight load must leave that load alone (it reads `autoPlay` once the
  // chunk is playable and starts the transport itself).
  const pause: AudioChunkPlayerActions['pause'] = () => {
    store.setState({ autoPlay: false });
    getPlayer().pause();
  };
  const play: AudioChunkPlayerActions['play'] = () => {
    store.setState({ autoPlay: true, isPlaybackComplete: false });
    // A play press on a faulted player is retry intent: reload the current
    // chunk (beginPlaybackCommand clears the fault and probes the circuit)
    // instead of playing an empty or stale source. The reload is the one
    // retargeting-shaped part of play, so it alone dispatches.
    if (store.getState().playbackFault !== null) {
      dispatchPlaybackCommand({ _tag: 'reloadChunk' });
      return;
    }
    if (store.getState().isLoadingAudio) return;
    runtime.runFork(
      startPlayback.pipe(Effect.annotateLogs(PLAYBACK_LOG_MODULE)),
    );
  };
  const togglePlayPause: AudioChunkPlayerActions['togglePlayPause'] = () => {
    // Fault outranks isPlaying: the fault UI wires its retry tap here,
    // and answering it with a pause would stop working audio.
    if (store.getState().playbackFault === null && getPlayer().isPlaying) {
      pause();
      return;
    }
    play();
  };

  const actions = {
    pause,
    play,
    togglePlayPause,
    loadAudio,
    unloadAudio: () => {
      // Interrupting both lanes is enough: any load in flight lives inside
      // one of their fibers, so nothing survives to write state back. The
      // pending boundary record dies with the session it belonged to.
      registry.set(commandAtom, Atom.Interrupt);
      registry.set(automatedCommandAtom, Atom.Interrupt);
      pendingAutomated = null;
      beginPlaybackCommand();
      logDetached(Effect.logInfo('Unload Audio'));
      getPlayer().stop();
      store.setState({
        ...initialPlaybackState,
        stats: resetPlaybackSessionStats(store.getState().stats),
      });
    },
    skipToChunk,
    skipToDocPackRange,
    reloadChunk,
    skipToChapterStart: (chapterId: string, loadParams?: CommonLoadParams) => {
      const documentSummary = getDocumentSummary();
      if (!documentSummary) {
        logDetached(
          Effect.logWarning('No document summary to skip to chapter start'),
        );
        return;
      }
      const chapter = documentSummary.chapters.find((c) => c.id === chapterId);
      if (!chapter) {
        // An imperative action, not a command fiber: a throw here would
        // surface as an unhandled error inside a React event handler.
        logDetached(
          Effect.logWarning('Chapter not found').pipe(
            Effect.annotateLogs({ chapterId }),
          ),
        );
        return;
      }

      if (getFreshDocPack(documentSummary)) {
        const anchor = docPackAnchorForLegacyChapterStart(
          documentSummary,
          chapter.id,
        );
        if (anchor) {
          skipToDocPackRange(anchor, loadParams);
          return;
        }
      }

      const firstAudioChunk = findFirstAudioChunkAtOrAfterChapter(
        documentSummary,
        chapter.id,
      );
      if (!firstAudioChunk) {
        logDetached(
          Effect.logWarning(
            'No playable audio chunk found at or after chapter',
          ).pipe(Effect.annotateLogs({ chapterId })),
        );
        return;
      }

      skipToChunk(firstAudioChunk.ttsId, loadParams);
    },
    loadAudioFromProgress: (audio: AudioItemSummary) => {
      logDetached(
        Effect.logInfo('Loading audio with progress').pipe(
          Effect.annotateLogs({
            audioId: audio.id,
            title: audio.title,
            progress: audio.progress,
          }),
        ),
      );
      const { chapterId, ttsId, loadParams } = audioProgressLoadArgs(
        audio.progress,
      );
      loadAudio(audio.id, chapterId, ttsId, loadParams);
    },
    loadAudioAtNote: (audioId: string, noteTarget: NotePlaybackTarget) => {
      dispatchPlaybackCommand({ _tag: 'loadAudio', audioId, noteTarget });
    },
  };
  return { commandAtom, automatedCommandAtom, actions };
};
